package com.arena_web.utils

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import org.w3c.fetch.Response

private const val CITY_CC_PREFIX = "city_cc:"
private const val TOMBSTONE_PREFIX = "t:"

// Re-query a failed city after a day. Earlier builds stored "" as a permanent
// tombstone, which meant a single transient Nominatim error (rate limit, 403,
// offline) locked the city flagless forever.
private const val TOMBSTONE_TTL_MS = 24L * 60 * 60 * 1000

// Nominatim usage policy caps anonymous traffic at 1 req/sec.
private const val MIN_FETCH_INTERVAL_MS = 1100L
private var lastFetchAtMs: Double = 0.0

private val inMemoryCache = mutableMapOf<String, String?>()

private data class StoredEntry(val code: String?, val expired: Boolean)

private fun nowMs(): Double = js("Date.now()") as Double

private fun parseStored(stored: String): StoredEntry {
    if (stored.startsWith(TOMBSTONE_PREFIX)) {
        val ts = stored.removePrefix(TOMBSTONE_PREFIX).toDoubleOrNull()
            ?: return StoredEntry(null, expired = true)
        return StoredEntry(null, expired = nowMs() - ts >= TOMBSTONE_TTL_MS)
    }
    // Legacy permanent tombstone — always re-query.
    if (stored.isBlank()) return StoredEntry(null, expired = true)
    return StoredEntry(stored, expired = false)
}

/**
 * Convert an ISO 3166-1 alpha-2 country code (e.g. "ua", "pl") into a flag
 * emoji built from regional-indicator code points. Returns an empty string if
 * the code is null, wrong length, or not made of A–Z letters.
 */
fun countryCodeToFlagEmoji(code: String?): String {
    if (code == null || code.length != 2) return ""
    val upper = code.uppercase()
    val a = upper[0].code
    val b = upper[1].code
    if (a !in 'A'.code..'Z'.code || b !in 'A'.code..'Z'.code) return ""
    val first = 0x1F1E6 + (a - 'A'.code)
    val second = 0x1F1E6 + (b - 'A'.code)
    return js("String.fromCodePoint")(first, second) as String
}

/**
 * Local flag asset URL for an ISO 3166-1 alpha-2 code — points at a bundled SVG
 * under `/flags/{cc}.svg` (flag-icons 4x3 set, shipped with the app). Renders
 * consistently on every platform, unlike flag emoji which Windows can't draw,
 * and needs no Nominatim round-trip. Returns "" for a null/invalid code.
 */
fun flagAssetUrl(code: String?): String {
    if (code == null || code.length != 2) return ""
    val cc = code.lowercase()
    if (cc[0] !in 'a'..'z' || cc[1] !in 'a'..'z') return ""
    return "/flags/$cc.svg"
}

/** Synchronous cache hit. Returns "" when no flag was found previously. */
fun cachedCityFlag(city: String): String? {
    val code = cachedCityCountryCode(city) ?: return null
    return countryCodeToFlagEmoji(code).ifEmpty { "" }
}

/** Synchronous lookup of the ISO country code from in-memory + localStorage cache. */
fun cachedCityCountryCode(city: String): String? {
    val key = city.trim().lowercase()
    if (key.isEmpty()) return null
    if (inMemoryCache.containsKey(key)) return inMemoryCache[key]
    val stored = Storage.get("$CITY_CC_PREFIX$key") ?: return null
    val entry = parseStored(stored)
    if (entry.expired) return null
    inMemoryCache[key] = entry.code
    return entry.code
}

/**
 * Resolve a city name to its ISO 3166-1 alpha-2 country code via Nominatim,
 * persisted in localStorage. Failures are tombstoned with a timestamp so we
 * retry after [TOMBSTONE_TTL_MS] instead of being stuck forever.
 */
suspend fun lookupCityCountryCode(city: String): String? {
    val key = city.trim().lowercase()
    if (key.isEmpty()) return null

    if (inMemoryCache.containsKey(key)) return inMemoryCache[key]

    val stored = Storage.get("$CITY_CC_PREFIX$key")
    if (stored != null) {
        val entry = parseStored(stored)
        if (!entry.expired) {
            inMemoryCache[key] = entry.code
            return entry.code
        }
    }

    val code = fetchCityCountryCode(city)
    inMemoryCache[key] = code
    if (code != null) {
        Storage.save("$CITY_CC_PREFIX$key", code)
    } else {
        Storage.save("$CITY_CC_PREFIX$key", "$TOMBSTONE_PREFIX${nowMs()}")
    }
    return code
}

private suspend fun fetchCityCountryCode(city: String): String? {
    val sinceLast = nowMs() - lastFetchAtMs
    if (sinceLast < MIN_FETCH_INTERVAL_MS) {
        delay((MIN_FETCH_INTERVAL_MS - sinceLast.toLong()).coerceAtLeast(0L))
    }
    lastFetchAtMs = nowMs()

    return try {
        val encoded = js("encodeURIComponent")(city) as String
        val url = "https://nominatim.openstreetmap.org/search?format=json&q=$encoded&addressdetails=1&limit=1&accept-language=en"
        val response: Response = window.fetch(url).await()
        if (!response.ok) return null
        val text = response.text().await()
        val results: dynamic = JSON.parse(text)
        val length = (results.length as? Int) ?: 0
        if (length == 0) return null
        val first: dynamic = results[0]
        (first?.address?.country_code as? String)?.lowercase()
    } catch (e: kotlinx.coroutines.CancellationException) {
        throw e
    } catch (e: Throwable) {
        // Throwable, not Exception: a rejected window.fetch throws a JS
        // TypeError, which is NOT kotlin.Exception in Kotlin/JS — an Exception
        // catch let it escape and cancel the caller's whole scope (a Nominatim
        // 429 froze the Arena page).
        console.log("City country-code lookup error: ${e.message}")
        null
    }
}
